/**
 * useLineSteps — each Sales Order line's Process Steps chips (lineSteps.js, Mike 2026-10-20), for
 * the desktop SO page's Line Items column and the phone order screen alike.
 *
 * `truthLines` = GET /sales_orders/{id}/truth `lines` (grouped entries; flattened here),
 * `productionData` = the order's job progress (soData.loadOrderTruth's production_data), `soId` =
 * the order shown (a manifest's pieces count for the order whose paper they are on).
 *
 * A part no job carries yet takes its steps from its routing: GET /parts/{id} once per part
 * (soData.loadPartRoutings), never again for the same part while the screen is open.
 * `shopDay` = the shop's (Central) day, kept current on a screen left open overnight, so a
 * manifest past its due-back date turns red; the desktop's Production Detail strip reads it too.
 *
 * Returns { lineStepsMap: sales_order_line_id → { steps, fromRouting }, shopDay }.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { lineStepsByLine } from "./lineSteps";
import { loadPartRoutings } from "./soData";
import { flattenTruthEntries } from "../../utils/salesOrderMath";
import { shopToday } from "../../utils/shopDate";

export const SHOP_DAY_REFRESH_MS = 10 * 60 * 1000;

export function useLineSteps(truthLines, productionData, soId = null) {
  const [partRoutings, setPartRoutings] = useState({});
  const routingAsked = useRef(new Set());
  const [shopDay, setShopDay] = useState(() => shopToday());
  useEffect(() => {
    const id = setInterval(() => setShopDay(shopToday()), SHOP_DAY_REFRESH_MS);
    return () => clearInterval(id);
  }, []);

  const flat = useMemo(() => flattenTruthEntries(truthLines), [truthLines]);
  const lineStepsMap = useMemo(
    () => lineStepsByLine(flat, productionData, partRoutings, { soId: soId ?? null, today: shopDay }),
    [flat, productionData, partRoutings, soId, shopDay],
  );

  useEffect(() => {
    const missing = flat
      .filter((l) => l && !l.consumable_id && l.part_id != null && l.sales_order_line_id != null)
      .filter((l) => !lineStepsMap[l.sales_order_line_id] && !routingAsked.current.has(l.part_id))
      .map((l) => l.part_id);
    if (missing.length === 0) return;
    missing.forEach((id) => routingAsked.current.add(id));
    loadPartRoutings(missing)
      .then((found) => {
        if (Object.keys(found).length) setPartRoutings((prev) => ({ ...prev, ...found }));
      })
      .catch(() => {});
  }, [flat, lineStepsMap]);

  return { lineStepsMap, shopDay };
}
